<template>
  <div class="marketing-stock-root" role="region" aria-labelledby="stock-products-title">
    <div class="ambient ambient-one" aria-hidden="true"></div>
    <div class="ambient ambient-two" aria-hidden="true"></div>

    <div class="stock-page">
      <header class="stock-hero">
        <div class="hero-copy">
          <div class="brand-line">
            <span class="brand-mark" aria-hidden="true">
              <v-icon color="#ffffff" size="22">mdi-package-variant-closed</v-icon>
            </span>
            <div>
              <div class="eyebrow">Marketinglager</div>
              <div v-if="currentStudioName" class="studio-context">{{ currentStudioName }}</div>
            </div>
          </div>
          <h1 id="stock-products-title">Schneller finden.<br /><span>Sicher entnehmen.</span></h1>
          <p class="hero-subtitle">Marketingmaterial scannen, Bestand prüfen und die Entnahme in wenigen Sekunden erfassen.</p>
        </div>

        <div class="hero-side">
          <v-btn
            v-if="userService.hasRight('d_manageMarketingStock')"
            to="/dashboard/communication/stock-admin"
            outlined
            class="admin-nav-btn"
            height="46">
            <v-icon left size="19">mdi-tune-variant</v-icon>
            Adminbereich
          </v-btn>

          <div class="hero-metrics" aria-label="Lagerübersicht">
            <div class="metric">
              <span class="metric-value">{{ availableProductsCount }}</span>
              <span class="metric-label">verfügbar</span>
            </div>
            <div class="metric">
              <span class="metric-value">{{ categoryOptions.length }}</span>
              <span class="metric-label">Kategorien</span>
            </div>
            <div class="metric" :class="{ 'metric-attention': lowStockCount > 0 }">
              <span class="metric-value">{{ lowStockCount }}</span>
              <span class="metric-label">knapp</span>
            </div>
          </div>
        </div>
      </header>

      <nav class="tab-shell" aria-label="Bereiche">
        <v-tabs v-model="activeTab" background-color="transparent" hide-slider grow dark class="tabs-nav">
          <v-tab class="stock-tab">
            <v-icon left size="19">mdi-package-variant-closed</v-icon>
            Entnehmen
            <span class="tab-count">{{ products.length }}</span>
          </v-tab>
          <v-tab class="stock-tab">
            <v-icon left size="19">mdi-history</v-icon>
            Meine Entnahmen
            <span v-if="myOrders.length" class="tab-count">{{ myOrders.length }}</span>
          </v-tab>
        </v-tabs>
      </nav>

      <v-tabs-items v-model="activeTab" dark class="stock-tab-items">
        <v-tab-item>
          <section class="command-panel" aria-label="Produktsuche und Scanner">
            <div class="search-wrap">
              <v-text-field
                v-model="searchQuery"
                label="Produkt oder Barcode suchen"
                placeholder="Name, Kategorie oder Nummer"
                prepend-inner-icon="mdi-magnify"
                outlined
                dark
                hide-details
                clearable
                color="primary"
                class="search-field"
                @keyup.enter="handleBarcodeSearch"></v-text-field>
              <span class="search-hint d-none d-lg-inline">Enter öffnet einen exakten Barcode-Treffer</span>
            </div>

            <div class="scan-actions">
              <input ref="fileInput" type="file" accept="image/*" class="visually-hidden" tabindex="-1" aria-hidden="true" @change="onFileSelected" />
              <v-btn color="primary" depressed height="52" class="primary-action" @click="openLiveScanner">
                <v-icon left>mdi-line-scan</v-icon>
                Live scannen
              </v-btn>
              <v-btn outlined height="52" class="secondary-action" :loading="scanningFile" @click="triggerCaptureFlow">
                <v-icon left>mdi-image-search-outline</v-icon>
                Foto auswerten
              </v-btn>
            </div>
          </section>

          <div v-if="products.length" class="category-rail" role="group" aria-label="Nach Kategorie filtern">
            <button
              type="button"
              class="category-pill"
              :class="{ active: selectedCategory === 'all' }"
              :aria-pressed="selectedCategory === 'all'"
              @click="selectedCategory = 'all'">
              Alle <span>{{ products.length }}</span>
            </button>
            <button
              v-for="category in categoryOptions"
              :key="category"
              type="button"
              class="category-pill"
              :class="{ active: selectedCategory === category }"
              :aria-pressed="selectedCategory === category"
              @click="selectedCategory = category">
              {{ category }} <span>{{ getCategoryCount(category) }}</span>
            </button>
          </div>

          <div class="results-heading">
            <div>
              <span class="section-kicker">Live-Bestand</span>
              <h2>{{ filteredProducts.length }} {{ filteredProducts.length === 1 ? "Produkt" : "Produkte" }}</h2>
            </div>
            <span v-if="!loading && filteredProducts.length" class="result-note"> Antippen, Menge wählen, fertig. </span>
          </div>

          <div v-if="loading" class="product-grid" role="status" aria-live="polite">
            <span class="visually-hidden">Produkte werden geladen</span>
            <v-skeleton-loader v-for="index in 8" :key="index" type="card" dark class="product-skeleton"></v-skeleton-loader>
          </div>

          <section v-else-if="filteredProducts.length === 0" class="empty-state">
            <span class="empty-icon" aria-hidden="true"><v-icon size="34">mdi-package-variant</v-icon></span>
            <h2>Kein passendes Produkt</h2>
            <p>Prüfe den Suchbegriff oder setze die Filter zurück.</p>
            <v-btn text color="primary" class="text-none" @click="resetProductFilters">
              <v-icon left size="18">mdi-filter-off-outline</v-icon>
              Filter zurücksetzen
            </v-btn>
          </section>

          <div v-else>
            <section v-for="category in categoriesList" :key="category" class="category-section">
              <div class="category-heading">
                <h3>{{ category }}</h3>
                <span>{{ getProductsByCategory(category).length }}</span>
              </div>

              <div class="product-grid">
                <article
                  v-for="product in getProductsByCategory(category)"
                  :key="product._id"
                  class="product-card"
                  tabindex="0"
                  role="button"
                  :aria-label="`${product.title} entnehmen, ${getStockStatusLabel(product)}`"
                  @click="selectProductForBooking(product)"
                  @keydown.enter="selectProductForBooking(product)"
                  @keydown.space.prevent="selectProductForBooking(product)">
                  <div class="product-topline">
                    <span class="product-symbol" aria-hidden="true">
                      <v-icon size="22">mdi-package-variant-closed</v-icon>
                    </span>
                    <span class="stock-status" :class="getStockStatusClass(product)">
                      <span class="status-dot"></span>
                      {{ getStockStatusLabel(product) }}
                    </span>
                  </div>

                  <div class="product-content">
                    <h4>{{ product.title }}</h4>
                    <span class="barcode">
                      <v-icon size="15">mdi-barcode</v-icon>
                      {{ product.barcode }}
                    </span>
                  </div>

                  <div class="product-footer">
                    <span>{{ product.stock !== null && product.stock <= 0 ? "Entnahme prüfen" : "Entnehmen" }}</span>
                    <span class="arrow-button" aria-hidden="true"><v-icon size="19">mdi-arrow-right</v-icon></span>
                  </div>
                </article>
              </div>
            </section>
          </div>
        </v-tab-item>

        <v-tab-item>
          <section class="history-panel">
            <div class="panel-heading">
              <div>
                <span class="section-kicker">Persönliches Protokoll</span>
                <h2>Meine Entnahmen</h2>
                <p>Entnahmen können innerhalb von 24 Stunden storniert werden.</p>
              </div>
              <v-text-field
                v-model="myOrdersSearch"
                label="Entnahmen durchsuchen"
                prepend-inner-icon="mdi-magnify"
                outlined
                dark
                dense
                hide-details
                clearable
                color="primary"
                class="history-search"></v-text-field>
            </div>

            <div v-if="loadingOrders" class="history-loading" role="status" aria-live="polite">
              <v-progress-circular indeterminate color="primary" width="3"></v-progress-circular>
              <span>Entnahmen werden geladen …</span>
            </div>

            <div v-else-if="filteredMyOrders.length === 0" class="empty-state history-empty">
              <span class="empty-icon" aria-hidden="true"><v-icon size="32">mdi-history</v-icon></span>
              <h2>{{ myOrders.length ? "Keine Treffer" : "Noch keine Entnahmen" }}</h2>
              <p>{{ myOrders.length ? "Passe deine Suche an." : "Deine nächsten Entnahmen erscheinen automatisch hier." }}</p>
            </div>

            <template v-else>
              <div class="history-cards d-md-none">
                <article v-for="item in filteredMyOrders" :key="item._id" class="history-card">
                  <div class="history-card-top">
                    <div>
                      <span class="history-date">{{ formatDateTime(item.createdAt) }}</span>
                      <h3>{{ item.productTitle }}</h3>
                    </div>
                    <v-btn
                      v-if="canDeleteMyOrder(item)"
                      icon
                      class="icon-action danger-action"
                      aria-label="Entnahme stornieren"
                      title="Entnahme stornieren"
                      @click="confirmCancelOrder(item)">
                      <v-icon size="20">mdi-undo-variant</v-icon>
                    </v-btn>
                  </div>
                  <span class="barcode"><v-icon size="15">mdi-barcode</v-icon>{{ item.productBarcode }}</span>
                  <div class="history-meta">
                    <div>
                      <span>Studio</span><strong>{{ item.studio ? item.studio.name : "–" }}</strong>
                    </div>
                    <div>
                      <span>Menge</span><strong>{{ item.quantity }}</strong>
                    </div>
                  </div>
                  <p v-if="item.comment" class="history-comment">“{{ item.comment }}”</p>
                  <div v-if="canDeleteMyOrder(item)" class="cancel-window">
                    <v-icon size="15">mdi-clock-outline</v-icon>
                    Stornierbar bis {{ formatCancellationDeadline(item.createdAt) }}
                  </div>
                </article>
              </div>

              <v-data-table
                :headers="myOrdersHeaders"
                :items="filteredMyOrders"
                :items-per-page="15"
                dark
                class="transparent-table d-none d-md-block"
                no-data-text="Noch keine Entnahmen"
                no-results-text="Keine passenden Entnahmen gefunden">
                <template v-slot:item.createdAt="{ item }">
                  <span class="table-date">{{ formatDateTime(item.createdAt) }}</span>
                </template>
                <template v-slot:item.productTitle="{ item }">
                  <div class="table-product">
                    <strong>{{ item.productTitle }}</strong>
                    <span class="barcode"><v-icon size="14">mdi-barcode</v-icon>{{ item.productBarcode }}</span>
                  </div>
                </template>
                <template v-slot:item.studio="{ item }">
                  <span>{{ item.studio ? item.studio.name : "–" }}</span>
                </template>
                <template v-slot:item.quantity="{ item }">
                  <span class="quantity-badge">{{ item.quantity }}</span>
                </template>
                <template v-slot:item.comment="{ item }">
                  <span v-if="item.comment" class="table-comment" :title="item.comment">{{ truncateText(item.comment, 52) }}</span>
                  <span v-else class="muted-dash">–</span>
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-btn
                    v-if="canDeleteMyOrder(item)"
                    icon
                    class="icon-action danger-action"
                    aria-label="Entnahme stornieren"
                    title="Entnahme stornieren"
                    @click="confirmCancelOrder(item)">
                    <v-icon size="19">mdi-undo-variant</v-icon>
                  </v-btn>
                  <span v-else class="locked-state" title="Stornofrist abgelaufen"><v-icon size="16">mdi-lock-outline</v-icon></span>
                </template>
              </v-data-table>
            </template>
          </section>
        </v-tab-item>
      </v-tabs-items>
    </div>

    <v-dialog
      v-model="liveScannerOpen"
      max-width="560"
      persistent
      :fullscreen="$vuetify.breakpoint.xsOnly"
      transition="dialog-bottom-transition"
      aria-labelledby="scanner-dialog-title">
      <v-card class="stock-sheet scanner-sheet" dark>
        <div class="sheet-header">
          <div>
            <span class="sheet-kicker">Kamera</span>
            <h2 id="scanner-dialog-title">Barcode live scannen</h2>
          </div>
          <v-btn icon class="icon-action" aria-label="Scanner schließen" @click="closeLiveScanner">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <div class="scanner-body">
          <div class="scanner-container">
            <video ref="scannerVideo" class="live-qr-reader" autoplay muted playsinline></video>
            <canvas ref="scannerCanvas" class="visually-hidden" aria-hidden="true"></canvas>
            <div class="scanner-frame" aria-hidden="true"></div>
            <div class="scanner-laser" aria-hidden="true"></div>
          </div>
          <div class="scanner-tip">
            <span class="scanner-tip-icon"><v-icon size="19">mdi-lightbulb-outline</v-icon></span>
            <p>
              <strong>{{ scannerStatus }}</strong
              ><br />Barcode mit sichtbarem weissem Rand ruhig in den Rahmen halten.
            </p>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="bookingDialogOpen"
      max-width="560"
      scrollable
      :fullscreen="$vuetify.breakpoint.xsOnly"
      transition="dialog-bottom-transition"
      aria-labelledby="booking-dialog-title">
      <v-card class="stock-sheet booking-sheet" dark>
        <div class="sheet-header">
          <div>
            <span class="sheet-kicker">Entnahme erfassen</span>
            <h2 id="booking-dialog-title">Produkt entnehmen</h2>
          </div>
          <v-btn icon class="icon-action" aria-label="Dialog schließen" @click="closeBookingDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text v-if="bookingProduct" class="sheet-content">
          <div class="selected-product">
            <span class="selected-product-icon" aria-hidden="true"><v-icon>mdi-package-variant-closed</v-icon></span>
            <div class="selected-product-copy">
              <span>{{ bookingProduct.category || "Allgemein" }}</span>
              <h3>{{ bookingProduct.title }}</h3>
              <span class="barcode"><v-icon size="15">mdi-barcode</v-icon>{{ bookingProduct.barcode }}</span>
            </div>
            <span class="stock-status" :class="getStockStatusClass(bookingProduct)">
              {{ getStockStatusLabel(bookingProduct) }}
            </span>
          </div>

          <v-form ref="bookingForm" v-model="bookingFormValid" class="booking-form">
            <v-autocomplete
              v-model="bookingStudio"
              :items="studioOptions"
              item-text="name"
              item-value="_id"
              label="Studio"
              placeholder="Studio suchen"
              prepend-inner-icon="mdi-map-marker-outline"
              :rules="[(v) => !!v || 'Bitte Studio auswählen']"
              :menu-props="{ contentClass: 'stock-products-menu' }"
              outlined
              color="primary"
              class="form-field"></v-autocomplete>

            <div class="field-group">
              <label for="booking-quantity">Menge</label>
              <div class="quantity-control">
                <v-btn icon class="quantity-button" :disabled="bookingQuantity <= 1" aria-label="Menge reduzieren" @click="decrementBookingQuantity">
                  <v-icon>mdi-minus</v-icon>
                </v-btn>
                <v-text-field
                  id="booking-quantity"
                  v-model.number="bookingQuantity"
                  type="number"
                  min="1"
                  solo
                  flat
                  hide-details
                  class="quantity-input"
                  aria-describedby="booking-quantity-error"
                  :aria-invalid="Boolean(bookingQuantityError)"
                  :rules="[
                    (v) => !!v || 'Bitte Anzahl eingeben',
                    (v) => Number(v) > 0 || 'Menge muss grösser als 0 sein',
                    (v) => Number.isInteger(Number(v)) || 'Menge muss eine ganze Zahl sein',
                  ]"></v-text-field>
                <v-btn icon class="quantity-button" aria-label="Menge erhöhen" @click="incrementBookingQuantity">
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </div>
              <span v-if="bookingQuantityError" id="booking-quantity-error" class="field-error" role="alert">{{ bookingQuantityError }}</span>
            </div>

            <div v-if="projectedStock !== null" class="stock-projection" :class="{ warning: projectedStock < 0 }">
              <span>
                <v-icon size="18">{{ projectedStock < 0 ? "mdi-alert-outline" : "mdi-chart-timeline-variant" }}</v-icon>
                Bestand nach Entnahme
              </span>
              <strong>{{ projectedStock }}</strong>
            </div>

            <v-textarea
              v-model="bookingComment"
              label="Kommentar (optional)"
              placeholder="Zum Beispiel Einsatzort oder Kampagne"
              prepend-inner-icon="mdi-message-text-outline"
              outlined
              rows="3"
              color="primary"
              auto-grow
              class="form-field"></v-textarea>
          </v-form>
        </v-card-text>

        <v-card-actions class="sheet-actions">
          <v-btn text class="cancel-action" @click="closeBookingDialog">Abbrechen</v-btn>
          <v-btn color="primary" depressed class="submit-action" :loading="submittingBooking" :disabled="!bookingFormValid" @click="submitBooking">
            <v-icon left size="19">mdi-check</v-icon>
            {{ bookingQuantity || 0 }} Artikel entnehmen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="cancelDialogOpen" max-width="440" transition="dialog-bottom-transition" aria-labelledby="cancel-dialog-title">
      <v-card class="stock-sheet confirm-sheet" dark>
        <div class="confirm-icon" aria-hidden="true"><v-icon size="26">mdi-undo-variant</v-icon></div>
        <v-card-title id="cancel-dialog-title">Entnahme stornieren?</v-card-title>
        <v-card-text v-if="orderToCancel">
          <strong>{{ orderToCancel.quantity }} × {{ orderToCancel.productTitle }}</strong> wird für
          <strong>{{ orderToCancel.studio ? orderToCancel.studio.name : "das gewählte Studio" }}</strong> storniert. Der Bestand wird automatisch
          wieder gutgeschrieben.
        </v-card-text>
        <v-card-actions class="sheet-actions confirm-actions">
          <v-btn text class="cancel-action" @click="closeCancelDialog">Behalten</v-btn>
          <v-btn color="error" depressed class="submit-action" :loading="cancelingOrder" @click="executeCancelOrder"> Jetzt stornieren </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex"
import api from "@/api"
import moment from "moment"
import userService from "@/services/userService"

const PRODUCT_BARCODE_SCRIPT_URL = "https://app.wellcomefit.ch/img/js/barcode.js"
const PRODUCT_BARCODE_WASM_URL = "/img/js/zxing_reader.wasm"
const PRODUCT_ZXING_MODULE_OVERRIDES = Object.freeze({
  locateFile: (fileName, scriptDirectory) => (fileName.endsWith(".wasm") ? PRODUCT_BARCODE_WASM_URL : `${scriptDirectory || ""}${fileName}`),
})

export default {
  name: "MarketingStockProducts",
  data() {
    return {
      userService,
      activeTab: 0,
      products: [],
      loading: false,
      searchQuery: "",
      selectedCategory: "all",

      // File Scan (analogue to creditCardBills)
      scanningFile: false,

      // Live Scan
      liveScannerOpen: false,
      scannerStream: null,
      scannerTimer: null,
      scannerDecodeBusy: false,
      scannerFrameIndex: 0,
      scannerStatus: "Bereit",
      scannerSessionId: 0,
      scannerCandidate: "",
      scannerCandidateCount: 0,
      scannerCandidateLastSeenAt: 0,
      scannerRejectedBarcode: "",
      scannerRejectedAt: 0,

      // Booking Dialog
      bookingDialogOpen: false,
      bookingProduct: null,
      bookingStudio: null,
      bookingQuantity: 1,
      bookingComment: "",
      bookingFormValid: false,
      submittingBooking: false,

      // User's withdrawal history
      myOrders: [],
      loadingOrders: false,
      myOrdersSearch: "",
      myOrdersHeaders: [
        { text: "Zeitpunkt", value: "createdAt", sortable: true, width: "140px" },
        { text: "Produkt", value: "productTitle", sortable: true },
        { text: "Studio", value: "studio", sortable: true },
        { text: "Menge", value: "quantity", sortable: true, width: "90px" },
        { text: "Kommentar", value: "comment", sortable: false },
        { text: "Aktionen", value: "actions", sortable: false, width: "80px", align: "end" },
      ],
      cancelDialogOpen: false,
      orderToCancel: null,
      cancelingOrder: false,
    }
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    ...mapGetters("studioStore", ["getSelectedStudioId"]),

    currentUserId() {
      return this.$store.state.userStore?.user?._id
    },

    currentStudioName() {
      const selectedStudio = (this.storeStudios || []).find((studio) => studio._id === this.getSelectedStudioId)
      return selectedStudio?.name || ""
    },

    studioOptions() {
      return (this.storeStudios || []).filter((s) => s.code && s.code !== "ot" && s.code !== "alibey").map((s) => ({ _id: s._id, name: s.name }))
    },

    categoryOptions() {
      const categories = this.products.map((product) => product.category || "Allgemein")
      return [...new Set(categories)].sort((a, b) => a.localeCompare(b, "de"))
    },

    availableProductsCount() {
      return this.products.filter((product) => product.stock === null || Number(product.stock) > 0).length
    },

    lowStockCount() {
      return this.products.filter((product) => this.isLowStock(product)).length
    },

    filteredProducts() {
      return this.products.filter((p) => {
        const category = p.category || "Allgemein"
        if (this.selectedCategory !== "all" && category !== this.selectedCategory) return false
        if (!this.searchQuery) return true

        const query = this.searchQuery.toLowerCase().trim()
        return [p.title, p.barcode, category].some((value) =>
          String(value || "")
            .toLowerCase()
            .includes(query),
        )
      })
    },

    categoriesList() {
      const cats = this.filteredProducts.map((p) => p.category || "Allgemein")
      return [...new Set(cats)].sort()
    },

    filteredMyOrders() {
      if (!this.myOrdersSearch) return this.myOrders
      const q = this.myOrdersSearch.toLowerCase().trim()
      return this.myOrders.filter((o) => {
        return [o.productTitle, o.productBarcode, o.studio?.name, o.comment].some((value) =>
          String(value || "")
            .toLowerCase()
            .includes(q),
        )
      })
    },

    projectedStock() {
      if (!this.bookingProduct || this.bookingProduct.stock === null || this.bookingQuantityError) return null
      return Number(this.bookingProduct.stock) - Number(this.bookingQuantity || 0)
    },

    bookingQuantityError() {
      if (this.bookingQuantity === "" || this.bookingQuantity === null || this.bookingQuantity === undefined) return "Bitte Menge eingeben"
      if (Number(this.bookingQuantity) <= 0) return "Die Menge muss grösser als 0 sein"
      if (!Number.isInteger(Number(this.bookingQuantity))) return "Bitte eine ganze Zahl eingeben"
      return ""
    },
  },
  watch: {
    activeTab(newTab) {
      if (newTab === 0) {
        this.loadProducts()
      } else if (newTab === 1) {
        this.loadMyOrders()
      }
    },
  },
  mounted() {
    this.loadProducts()
    this.bookingStudio = this.getSelectedStudioId || null
    this.warmBarcodeScanner()
  },
  beforeDestroy() {
    this.closeLiveScanner()
  },
  methods: {
    async loadProducts() {
      this.loading = true
      try {
        this.products = await api.marketingStock.fetchProducts()
        if (this.selectedCategory !== "all" && !this.categoryOptions.includes(this.selectedCategory)) {
          this.selectedCategory = "all"
        }
      } catch (err) {
        this.$toast.error("Produkte konnten nicht geladen werden")
        console.error(err)
      } finally {
        this.loading = false
      }
    },

    getProductsByCategory(category) {
      return this.filteredProducts.filter((p) => (p.category || "Allgemein") === category)
    },

    getCategoryCount(category) {
      return this.products.filter((product) => (product.category || "Allgemein") === category).length
    },

    resetProductFilters() {
      this.searchQuery = ""
      this.selectedCategory = "all"
    },

    isLowStock(product) {
      if (!product || product.stock === null || Number(product.stock) <= 0) return false
      const threshold =
        product.notificationThreshold !== null && product.notificationThreshold !== undefined ? Number(product.notificationThreshold) : 5
      return Number(product.stock) < threshold
    },

    getStockStatusClass(product) {
      const stock = product?.stock
      if (stock === null || stock === undefined) return "status-untracked"
      if (Number(stock) <= 0) return "status-empty"
      if (this.isLowStock(product)) return "status-low"
      return "status-ready"
    },

    getStockStatusLabel(product) {
      const stock = product?.stock
      if (stock === null || stock === undefined) return "Nicht gezählt"
      if (Number(stock) < 0) return `Fehlbestand · ${stock}`
      if (Number(stock) === 0) return "Leer · 0"
      if (this.isLowStock(product)) return `Knapp · ${stock}`
      return `Verfügbar · ${stock}`
    },

    // --- SEARCH / BARCODE SCANNED AUTO-DETECTION ---
    handleBarcodeSearch() {
      if (!this.searchQuery) return

      // Check if query is an exact match for a barcode
      const match = this.products.find((p) => p.barcode === this.searchQuery.trim())
      if (match) {
        this.selectProductForBooking(match)
        this.searchQuery = "" // Reset search query
      }
    },

    selectProductForBooking(product) {
      this.bookingProduct = product
      this.bookingQuantity = 1
      this.bookingComment = ""
      this.bookingStudio = this.getSelectedStudioId || this.bookingStudio || null
      this.bookingDialogOpen = true
    },

    closeBookingDialog() {
      this.bookingDialogOpen = false
      this.bookingProduct = null
    },

    decrementBookingQuantity() {
      this.bookingQuantity = Math.max(1, Number(this.bookingQuantity || 1) - 1)
    },

    incrementBookingQuantity() {
      this.bookingQuantity = Math.max(1, Number(this.bookingQuantity || 0) + 1)
    },

    async submitBooking() {
      if (!this.$refs.bookingForm.validate()) return

      this.submittingBooking = true
      try {
        const payload = {
          productId: this.bookingProduct._id,
          quantity: Number(this.bookingQuantity),
          studioId: this.bookingStudio,
          comment: this.bookingComment,
        }

        await api.marketingStock.createOrder(payload)

        this.$toast.success("Entnahme erfolgreich erfasst")
        this.closeBookingDialog()

        // Reload products to get latest stock level
        await this.loadProducts()
        if (this.currentUserId) {
          this.loadMyOrders()
        }
      } catch (err) {
        const errMsg = err.response?.data?.error || "Fehler beim Erfassen der Entnahme"
        this.$toast.error(errMsg)
        console.error(err)
      } finally {
        this.submittingBooking = false
      }
    },

    // --- FILE CAMERA SCANNING FLOW (LIKE CREDIT CARD BILLS) ---
    triggerCaptureFlow() {
      if (this.$vuetify.breakpoint.xsOnly && window.flutter_inappwebview) {
        // iOS/Android mobile app webview capture
        window.flutter_inappwebview
          .callHandler("openCameraNative")
          .then((result) => {
            if (result && result.success) {
              const file = this.dataURLtoFile(result.base64, result.name || "barcode_capture.jpg")
              this.processBarcodeFromImage(file)
            }
          })
          .catch(() => {
            this.$refs.fileInput.click()
          })
      } else {
        // Fallback for regular web browsers
        this.$refs.fileInput.click()
      }
    },

    dataURLtoFile(dataurl, filename) {
      const arr = dataurl.split(",")
      const mime = arr[0].match(/:(.*?);/)[1]
      const bstr = atob(arr[1])
      let n = bstr.length
      const u8arr = new Uint8Array(n)
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
      }
      return new File([u8arr], filename, { type: mime })
    },

    onFileSelected(event) {
      const file = event.target.files[0]
      if (file) {
        this.processBarcodeFromImage(file)
      }
      // Reset input value to allow selecting same file again
      event.target.value = ""
    },

    async processBarcodeFromImage(file) {
      this.scanningFile = true
      try {
        await this.loadZxingWasm()
        const results = await window.ZXingWASM.readBarcodes(file, this.getScannerReaderOptions())
        const result = (results || []).find((entry) => this.isSupportedScannerResult(entry))
        if (!result) throw new Error("Kein gültiger EAN-13- oder Code-128-Barcode erkannt")
        this.handleScannedBarcode(String(result.text || "").trim())
      } catch (err) {
        this.$toast.error("Kein gültiger Barcode auf dem Bild erkannt. Bitte deutlicher fotografieren.")
        console.error("ZXing-WASM file scan failed:", err)
      } finally {
        this.scanningFile = false
      }
    },

    // --- LIVE CAMERA SCANNING ---
    getScannerReaderOptions(overrides = {}) {
      return {
        formats: ["EAN13", "Code128"],
        tryHarder: true,
        tryRotate: true,
        tryInvert: true,
        tryDownscale: false,
        minLineCount: 1,
        maxNumberOfSymbols: 1,
        ...overrides,
      }
    },

    warmBarcodeScanner() {
      this.loadZxingWasm()
        .then(() => window.ZXingWASM.getZXingModule())
        .catch((error) => console.warn("Barcode-Scanner konnte nicht vorgewärmt werden:", error))
    },

    async openLiveScanner() {
      const scannerSessionId = ++this.scannerSessionId
      this.resetScannerConsensus()
      this.scannerFrameIndex = 0
      this.scannerStatus = "Scanner wird vorbereitet …"
      this.liveScannerOpen = true
      try {
        await this.loadZxingWasm()

        if (!this.liveScannerOpen || scannerSessionId !== this.scannerSessionId) return

        this.$nextTick(async () => {
          if (!this.liveScannerOpen || scannerSessionId !== this.scannerSessionId) return
          try {
            const stream = await this.openScannerCameraStream()
            if (!this.liveScannerOpen || scannerSessionId !== this.scannerSessionId) {
              stream.getTracks().forEach((track) => track.stop())
              return
            }

            this.scannerStream = stream
            const video = this.$refs.scannerVideo
            if (!video) throw new Error("Videoelement ist nicht verfügbar")
            video.srcObject = stream
            await video.play()
            await this.optimizeScannerCamera(stream)
            this.scannerStatus = "EAN-13 oder Code 128 in den Rahmen halten"
            this.scheduleWasmScan(scannerSessionId, 0)
          } catch (startErr) {
            this.stopScannerStream()
            if (scannerSessionId !== this.scannerSessionId) return
            this.$toast.error("Kamera konnte nicht gestartet werden. Bitte Berechtigungen prüfen.")
            console.error("Camera start failed:", startErr)
            this.liveScannerOpen = false
          }
        })
      } catch (err) {
        if (scannerSessionId !== this.scannerSessionId) return
        this.$toast.error("Kamera konnte nicht initialisiert werden")
        console.error(err)
        this.liveScannerOpen = false
      }
    },

    closeLiveScanner() {
      this.liveScannerOpen = false
      this.scannerSessionId += 1
      this.resetScannerConsensus()
      this.stopScannerStream()
    },

    async openScannerCameraStream() {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("Dieser Browser unterstützt keinen Kamerazugriff")
      }

      try {
        return await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        })
      } catch (preferredCameraError) {
        console.debug("Optimierte Kameraeinstellung nicht verfügbar:", preferredCameraError)
        return navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { facingMode: "environment" },
        })
      }
    },

    stopScannerStream() {
      if (this.scannerTimer) {
        window.clearTimeout(this.scannerTimer)
        this.scannerTimer = null
      }
      this.scannerDecodeBusy = false

      if (this.scannerStream) {
        this.scannerStream.getTracks().forEach((track) => track.stop())
        this.scannerStream = null
      }

      const video = this.$refs.scannerVideo
      if (video) video.srcObject = null
    },

    async optimizeScannerCamera(stream) {
      const track = stream?.getVideoTracks?.()[0]
      if (!track || typeof track.getCapabilities !== "function") return

      try {
        const capabilities = track.getCapabilities()
        const settings = typeof track.getSettings === "function" ? track.getSettings() : {}
        const advancedConstraints = {}

        if (Array.isArray(capabilities.focusMode) && capabilities.focusMode.includes("continuous")) {
          advancedConstraints.focusMode = "continuous"
        }
        if (Array.isArray(capabilities.exposureMode) && capabilities.exposureMode.includes("continuous")) {
          advancedConstraints.exposureMode = "continuous"
        }
        if (Array.isArray(capabilities.whiteBalanceMode) && capabilities.whiteBalanceMode.includes("continuous")) {
          advancedConstraints.whiteBalanceMode = "continuous"
        }
        if (
          capabilities.zoom &&
          Number.isFinite(capabilities.zoom.min) &&
          Number.isFinite(capabilities.zoom.max) &&
          capabilities.zoom.max > capabilities.zoom.min
        ) {
          const currentZoom = Number.isFinite(settings.zoom) ? settings.zoom : capabilities.zoom.min
          const preferredZoom = Math.min(capabilities.zoom.max, Math.max(capabilities.zoom.min, currentZoom, 1.15))
          if (preferredZoom > currentZoom) advancedConstraints.zoom = preferredZoom
        }

        if (Object.keys(advancedConstraints).length && typeof track.applyConstraints === "function") {
          await track.applyConstraints({ advanced: [advancedConstraints] })
        }
      } catch (constraintError) {
        console.debug("Zusätzliche Kameraoptimierung nicht verfügbar:", constraintError)
      }
    },

    scheduleWasmScan(scannerSessionId, delay = 70) {
      if (!this.liveScannerOpen || scannerSessionId !== this.scannerSessionId) return
      if (this.scannerTimer) window.clearTimeout(this.scannerTimer)
      this.scannerTimer = window.setTimeout(() => {
        this.scannerTimer = null
        this.scanWasmFrame(scannerSessionId)
      }, delay)
    },

    async scanWasmFrame(scannerSessionId) {
      if (!this.liveScannerOpen || scannerSessionId !== this.scannerSessionId) return
      if (this.scannerDecodeBusy) {
        this.scheduleWasmScan(scannerSessionId)
        return
      }

      const video = this.$refs.scannerVideo
      const canvas = this.$refs.scannerCanvas
      if (!video || !canvas || video.readyState < 2 || !video.videoWidth || !video.videoHeight) {
        this.scheduleWasmScan(scannerSessionId)
        return
      }

      this.scannerFrameIndex += 1
      const shouldRunFallback = Boolean(this.scannerCandidate) || this.scannerFrameIndex % 3 === 0
      const scanPasses = [
        {
          label: "Fokusbereich",
          x: 0.05,
          y: 0.25,
          width: 0.9,
          height: 0.5,
          binarizer: "LocalAverage",
          maxWidth: 1080,
          readerOptions: { tryHarder: true, tryRotate: false, tryInvert: false },
        },
      ]

      if (shouldRunFallback) {
        scanPasses.push(
          {
            label: "erweiterter Rahmen",
            x: 0.03,
            y: 0.19,
            width: 0.94,
            height: 0.62,
            binarizer: "LocalAverage",
            maxWidth: 1080,
            readerOptions: { tryHarder: true, tryRotate: true, tryInvert: false },
          },
          {
            label: "Gesamtbild",
            x: 0,
            y: 0,
            width: 1,
            height: 1,
            binarizer: "GlobalHistogram",
            maxWidth: 960,
            readerOptions: { tryHarder: true, tryRotate: true, tryInvert: true },
          },
        )
      }

      this.scannerDecodeBusy = true
      try {
        let results = []
        let successfulPass = null
        for (const scanPass of scanPasses) {
          results = await this.decodeScannerVideoRegion(video, canvas, scanPass)
          if (Array.isArray(results) && results.length) {
            successfulPass = scanPass.label
            break
          }
        }

        if (!this.liveScannerOpen || scannerSessionId !== this.scannerSessionId) return
        const result = (results || []).find((entry) => this.isSupportedScannerResult(entry))
        if (result) {
          this.registerLiveScanCandidate(result.text, { ...result, scanPass: successfulPass }, scannerSessionId)
        } else {
          this.registerScannerMiss()
        }
      } catch (decodeError) {
        this.registerScannerMiss()
        console.debug("ZXing-WASM scan miss:", decodeError)
      } finally {
        this.scannerDecodeBusy = false
        this.scheduleWasmScan(scannerSessionId)
      }
    },

    async decodeScannerVideoRegion(video, canvas, scanPass) {
      const sourceWidth = video.videoWidth
      const sourceHeight = video.videoHeight
      const sourceX = Math.floor(sourceWidth * scanPass.x)
      const sourceY = Math.floor(sourceHeight * scanPass.y)
      const regionWidth = Math.max(1, Math.floor(sourceWidth * scanPass.width))
      const regionHeight = Math.max(1, Math.floor(sourceHeight * scanPass.height))
      const renderScale = Math.min(1, (scanPass.maxWidth || regionWidth) / regionWidth)
      const renderWidth = Math.max(1, Math.round(regionWidth * renderScale))
      const renderHeight = Math.max(1, Math.round(regionHeight * renderScale))
      canvas.width = renderWidth
      canvas.height = renderHeight

      const context = canvas.getContext("2d", { willReadFrequently: true })
      if (!context) throw new Error("Canvas-Kontext nicht verfügbar")
      context.drawImage(video, sourceX, sourceY, regionWidth, regionHeight, 0, 0, renderWidth, renderHeight)
      const imageData = context.getImageData(0, 0, renderWidth, renderHeight)
      return window.ZXingWASM.readBarcodes(imageData, {
        ...this.getScannerReaderOptions(scanPass.readerOptions),
        binarizer: scanPass.binarizer,
      })
    },

    resetScannerConsensus() {
      this.scannerCandidate = ""
      this.scannerCandidateCount = 0
      this.scannerCandidateLastSeenAt = 0
    },

    registerScannerMiss() {
      const now = Date.now()
      if (this.scannerCandidate && now - this.scannerCandidateLastSeenAt > 3000) {
        this.resetScannerConsensus()
      }
      this.scannerStatus = "Suche läuft – Barcode ruhig halten"
    },

    normalizeScannerFormat(format) {
      return String(format || "")
        .toUpperCase()
        .replace(/[\s_-]/g, "")
    },

    isValidEan13(barcode) {
      if (!/^\d{13}$/.test(barcode)) return false
      const payload = barcode.slice(0, 12)
      const weightedSum = payload.split("").reduce((sum, digit, index) => sum + Number(digit) * (index % 2 === 0 ? 1 : 3), 0)
      return (10 - (weightedSum % 10)) % 10 === Number(barcode[12])
    },

    isSupportedScannerResult(result) {
      const barcode = String(result?.text || "").trim()
      let format = this.normalizeScannerFormat(result?.format)
      if (!format && this.isValidEan13(barcode)) format = "EAN13"
      if (!["EAN13", "CODE128"].includes(format)) return false
      return format !== "EAN13" || this.isValidEan13(barcode)
    },

    registerLiveScanCandidate(decodedText, decodedResult, scannerSessionId) {
      const barcode = String(decodedText || "").trim()
      if (!barcode || scannerSessionId !== this.scannerSessionId) return

      let format = this.normalizeScannerFormat(decodedResult?.format)
      if (!format && this.isValidEan13(barcode)) format = "EAN13"
      if (!["EAN13", "CODE128"].includes(format)) return
      if (format === "EAN13" && !this.isValidEan13(barcode)) return

      const now = Date.now()
      const belongsToCurrentSequence = barcode === this.scannerCandidate && now - this.scannerCandidateLastSeenAt <= 3000

      if (belongsToCurrentSequence) {
        this.scannerCandidateCount += 1
      } else {
        this.scannerCandidate = barcode
        this.scannerCandidateCount = 1
      }
      this.scannerCandidateLastSeenAt = now
      const requiredConfirmations = format === "EAN13" ? 2 : 3
      this.scannerStatus = `Barcode erkannt – Bestätigung ${this.scannerCandidateCount}/${requiredConfirmations}`

      if (this.scannerCandidateCount < requiredConfirmations) return

      const match = this.products.find((product) => String(product.barcode || "").trim() === barcode)
      if (!match) {
        this.resetScannerConsensus()
        if (barcode !== this.scannerRejectedBarcode || now - this.scannerRejectedAt > 2500) {
          this.scannerRejectedBarcode = barcode
          this.scannerRejectedAt = now
          this.$toast.error(`Barcode '${barcode}' ist keinem aktiven Lagerprodukt zugeordnet.`)
        }
        return
      }

      this.closeLiveScanner()
      this.selectProductForBooking(match)
    },

    handleScannedBarcode(barcode) {
      const match = this.products.find((p) => p.barcode === barcode)
      if (match) {
        this.selectProductForBooking(match)
      } else {
        this.$toast.error(`Barcode '${barcode}' erkannt, aber kein passendes Produkt im Lager gefunden.`)
      }
    },

    loadZxingWasm() {
      const configureWasm = () => {
        if (!window.ZXingWASM?.readBarcodes || !window.ZXingWASM?.setZXingModuleOverrides) {
          throw new Error("ZXing-WASM stellt keinen vollständigen Reader bereit")
        }
        window.ZXingWASM.setZXingModuleOverrides(PRODUCT_ZXING_MODULE_OVERRIDES)
      }

      if (window.ZXingWASM?.readBarcodes) {
        try {
          configureWasm()
          return Promise.resolve()
        } catch (error) {
          return Promise.reject(error)
        }
      }
      if (window.__lagerZxingWasmLoadingPromise) return window.__lagerZxingWasmLoadingPromise

      window.__lagerZxingWasmLoadingPromise = new Promise((resolve, reject) => {
        const script = document.createElement("script")
        script.src = PRODUCT_BARCODE_SCRIPT_URL
        script.type = "text/javascript"
        script.onload = () => {
          try {
            configureWasm()
            resolve()
          } catch (error) {
            window.__lagerZxingWasmLoadingPromise = null
            reject(error)
          }
        }
        script.onerror = (error) => {
          window.__lagerZxingWasmLoadingPromise = null
          reject(error)
        }
        document.head.appendChild(script)
      })

      return window.__lagerZxingWasmLoadingPromise
    },

    async loadMyOrders() {
      if (!this.currentUserId) return
      this.loadingOrders = true
      try {
        this.myOrders = await api.marketingStock.fetchOrders({ createdBy: this.currentUserId })
      } catch (err) {
        this.$toast.error("Meine Entnahmen konnten nicht geladen werden")
        console.error(err)
      } finally {
        this.loadingOrders = false
      }
    },

    formatDateTime(d) {
      return d ? moment(d).format("DD.MM.YYYY HH:mm") : ""
    },

    formatCancellationDeadline(d) {
      return d ? moment(d).add(24, "hours").format("DD.MM., HH:mm") : ""
    },

    truncateText(text, length) {
      if (!text) return ""
      if (text.length <= length) return text
      return text.substring(0, length) + "..."
    },

    // --- CANCELLATION METHODS ---
    canDeleteMyOrder(order) {
      if (!order || !order.createdAt) return false
      const diffMs = new Date() - new Date(order.createdAt)
      const diffHours = diffMs / (1000 * 60 * 60)
      return diffHours < 24
    },

    confirmCancelOrder(order) {
      this.orderToCancel = order
      this.cancelDialogOpen = true
    },

    closeCancelDialog() {
      this.cancelDialogOpen = false
      this.orderToCancel = null
    },

    async executeCancelOrder() {
      if (!this.orderToCancel) return

      this.cancelingOrder = true
      try {
        await api.marketingStock.deleteOrder(this.orderToCancel._id)
        this.$toast.success("Entnahme erfolgreich storniert")
        this.closeCancelDialog()

        // Refresh products and orders
        await this.loadProducts()
        await this.loadMyOrders()
      } catch (err) {
        const errMsg = err.response?.data?.error || "Fehler beim Stornieren der Entnahme"
        this.$toast.error(errMsg)
        console.error(err)
      } finally {
        this.cancelingOrder = false
      }
    },
  },
}
</script>

<style scoped>
.marketing-stock-root {
  --stock-bg: #1f201f;
  --stock-panel: #252625;
  --stock-panel-raised: #2a2b2a;
  --stock-line: rgba(255, 255, 255, 0.1);
  --stock-line-strong: rgba(255, 255, 255, 0.18);
  --stock-text: #fbfbfb;
  --stock-muted: #a3a6a3;
  --stock-accent: #42944a;
  --stock-accent-soft: rgba(66, 148, 74, 0.14);
  min-height: 100%;
  width: 100%;
  color: var(--stock-text);
  background: radial-gradient(circle at 82% -10%, rgba(66, 148, 74, 0.13), transparent 34%),
    linear-gradient(180deg, #1f201f 0%, #212121 52%, #1f201f 100%);
  position: relative;
  isolation: isolate;
  overflow-x: hidden;
}

.marketing-stock-root * {
  box-sizing: border-box;
}

.ambient {
  position: absolute;
  z-index: -1;
  border-radius: 999px;
  filter: blur(12px);
  pointer-events: none;
}

.ambient-one {
  width: 420px;
  height: 420px;
  top: 24%;
  left: -310px;
  background: rgba(66, 148, 74, 0.08);
}

.ambient-two {
  width: 360px;
  height: 360px;
  right: -280px;
  bottom: 6%;
  background: rgba(49, 93, 53, 0.08);
}

.stock-page {
  width: 100%;
  max-width: 1540px;
  margin: 0 auto;
  padding: clamp(14px, 2.2vw, 34px);
  padding-bottom: max(32px, env(safe-area-inset-bottom));
}

.stock-hero {
  min-height: 258px;
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(330px, 0.6fr);
  gap: 40px;
  align-items: stretch;
  padding: clamp(24px, 3.4vw, 52px);
  border: 1px solid var(--stock-line);
  border-radius: 28px;
  background: linear-gradient(115deg, rgba(38, 39, 38, 0.98), rgba(30, 31, 30, 0.94)),
    radial-gradient(circle at 85% 30%, rgba(66, 148, 74, 0.16), transparent 38%);
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.24);
  position: relative;
  overflow: hidden;
}

.stock-hero::after {
  content: "";
  width: 360px;
  height: 360px;
  position: absolute;
  right: -90px;
  top: -180px;
  border: 1px solid rgba(66, 148, 74, 0.2);
  border-radius: 50%;
  box-shadow: 0 0 0 44px rgba(66, 148, 74, 0.035), 0 0 0 88px rgba(66, 148, 74, 0.02);
  pointer-events: none;
}

.hero-copy,
.hero-side {
  position: relative;
  z-index: 1;
}

.hero-copy {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.brand-line {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
}

.brand-mark {
  width: 42px;
  height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 13px;
  background: #42944a;
  box-shadow: 0 10px 30px rgba(66, 148, 74, 0.22);
}

.eyebrow,
.section-kicker,
.sheet-kicker {
  color: #73b77a;
  font-size: 11px;
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.studio-context {
  margin-top: 3px;
  color: var(--stock-muted);
  font-size: 13px;
  line-height: 1.2;
}

.stock-hero h1 {
  max-width: 820px;
  margin: 0;
  color: var(--stock-text);
  font-size: clamp(38px, 4.5vw, 66px);
  font-weight: 850;
  line-height: 0.98;
  letter-spacing: -0.055em;
}

.stock-hero h1 span {
  color: var(--stock-accent);
}

.hero-subtitle {
  max-width: 650px;
  margin: 20px 0 0;
  color: #b1b4b1;
  font-size: clamp(15px, 1.3vw, 18px);
  line-height: 1.55;
}

.hero-side {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
  gap: 28px;
}

.admin-nav-btn {
  min-width: 170px;
  color: #f1f4f1 !important;
  border-color: rgba(66, 148, 74, 0.58) !important;
  border-radius: 13px !important;
  text-transform: none !important;
  font-weight: 700;
  letter-spacing: 0 !important;
}

.hero-metrics {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  border: 1px solid var(--stock-line);
  border-radius: 18px;
  background: rgba(4, 13, 20, 0.42);
  overflow: hidden;
}

.metric {
  min-width: 0;
  padding: 18px 14px;
  text-align: center;
}

.metric + .metric {
  border-left: 1px solid var(--stock-line);
}

.metric-value,
.metric-label {
  display: block;
}

.metric-value {
  color: #fbfbfb;
  font-size: 24px;
  font-weight: 800;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.metric-label {
  margin-top: 6px;
  color: var(--stock-muted);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.metric-attention .metric-value {
  color: #ffd37d;
}

.tab-shell {
  max-width: 690px;
  margin: 18px auto 30px;
  padding: 5px;
  border: 1px solid var(--stock-line);
  border-radius: 17px;
  background: rgba(29, 30, 29, 0.94);
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.18);
}

.tabs-nav >>> .v-tabs-bar {
  height: 50px;
  background: transparent !important;
}

.stock-tab {
  min-width: 0;
  border-radius: 12px;
  color: #919491 !important;
  font-size: 13px;
  font-weight: 750;
  letter-spacing: 0 !important;
  text-transform: none;
  transition: color 180ms ease, background-color 180ms ease;
}

.stock-tab.v-tab--active {
  color: #f8faf8 !important;
  background: #29352b;
  box-shadow: inset 0 0 0 1px rgba(66, 148, 74, 0.28), 0 6px 18px rgba(0, 0, 0, 0.14);
}

.tab-count {
  min-width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 9px;
  padding: 0 6px;
  border-radius: 7px;
  color: #d2d7d2;
  background: rgba(255, 255, 255, 0.08);
  font-size: 11px;
  font-variant-numeric: tabular-nums;
}

.stock-tab-items,
.stock-tab-items >>> .v-window__container,
.stock-tab-items >>> .v-window-item {
  overflow: visible !important;
  background: transparent !important;
}

.command-panel {
  display: grid;
  grid-template-columns: minmax(280px, 1fr) auto;
  align-items: start;
  gap: 16px;
  padding: 14px;
  border: 1px solid var(--stock-line);
  border-radius: 20px;
  background: rgba(38, 39, 38, 0.94);
  box-shadow: 0 16px 42px rgba(0, 0, 0, 0.16);
}

.search-wrap {
  min-width: 0;
}

.search-hint {
  margin: 7px 0 0 15px;
  color: #858885;
  font-size: 11px;
}

.search-field >>> .v-input__slot,
.history-search >>> .v-input__slot {
  min-height: 52px !important;
  border-radius: 13px !important;
  background: rgba(25, 26, 25, 0.72) !important;
}

.search-field >>> fieldset,
.history-search >>> fieldset,
.form-field >>> fieldset {
  border-color: rgba(255, 255, 255, 0.18) !important;
}

.search-field.v-input--is-focused >>> fieldset,
.history-search.v-input--is-focused >>> fieldset,
.form-field.v-input--is-focused >>> fieldset {
  border-color: #42944a !important;
  border-width: 2px !important;
}

.search-field >>> .v-label,
.history-search >>> .v-label,
.form-field >>> .v-label {
  color: #a6aaa6 !important;
}

.search-field >>> .v-icon,
.history-search >>> .v-icon,
.form-field >>> .v-icon {
  color: #8e928e;
}

.scan-actions {
  display: flex;
  gap: 10px;
}

.primary-action,
.secondary-action,
.cancel-action,
.submit-action {
  border-radius: 13px !important;
  text-transform: none !important;
  font-weight: 750 !important;
  letter-spacing: 0 !important;
}

.primary-action {
  color: #ffffff !important;
  background: #42944a !important;
  box-shadow: 0 10px 24px rgba(66, 148, 74, 0.24) !important;
}

.secondary-action {
  color: #e5e8e5 !important;
  border-color: rgba(255, 255, 255, 0.24) !important;
}

.category-rail {
  display: flex;
  gap: 8px;
  margin: 22px 0 30px;
  padding: 1px 0 7px;
  overflow-x: auto;
  overscroll-behavior-inline: contain;
  scrollbar-width: none;
}

.category-rail::-webkit-scrollbar {
  display: none;
}

.category-pill {
  min-height: 44px;
  flex: 0 0 auto;
  padding: 0 14px;
  border: 1px solid var(--stock-line);
  border-radius: 12px;
  color: #aeb1ae;
  background: rgba(24, 25, 24, 0.72);
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: color 160ms ease, border-color 160ms ease, background-color 160ms ease;
}

.category-pill span {
  margin-left: 6px;
  color: #858885;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
}

.category-pill.active {
  color: #edf5ee;
  border-color: rgba(66, 148, 74, 0.58);
  background: var(--stock-accent-soft);
}

.category-pill.active span {
  color: #7fbe86;
}

.results-heading,
.panel-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
}

.results-heading {
  margin-bottom: 18px;
}

.results-heading h2,
.panel-heading h2 {
  margin: 5px 0 0;
  color: var(--stock-text);
  font-size: clamp(23px, 2.2vw, 31px);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.025em;
}

.result-note {
  color: var(--stock-muted);
  font-size: 13px;
}

.category-section + .category-section {
  margin-top: 38px;
}

.category-heading {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 13px;
}

.category-heading h3 {
  margin: 0;
  color: #d5d8d5;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.category-heading > span {
  min-width: 23px;
  height: 23px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  border-radius: 7px;
  color: #989c98;
  background: rgba(255, 255, 255, 0.07);
  font-size: 11px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

.product-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

.product-card {
  min-width: 0;
  min-height: 220px;
  display: flex;
  flex-direction: column;
  padding: 18px;
  border: 1px solid var(--stock-line);
  border-radius: 18px;
  color: var(--stock-text);
  background: linear-gradient(150deg, rgba(43, 44, 43, 0.98), rgba(35, 36, 35, 0.98));
  box-shadow: 0 14px 36px rgba(0, 0, 0, 0.11);
  cursor: pointer;
  outline: 2px solid transparent;
  outline-offset: 2px;
  transition: transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease;
}

.product-topline,
.product-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.product-symbol,
.selected-product-icon,
.empty-icon,
.scanner-tip-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.product-symbol {
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 13px;
  color: #92ae95;
  background: rgba(255, 255, 255, 0.055);
}

.product-symbol .v-icon,
.arrow-button .v-icon,
.empty-icon .v-icon,
.scanner-tip-icon .v-icon,
.selected-product-icon .v-icon {
  color: inherit !important;
}

.stock-status {
  min-height: 28px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 9px;
  border: 1px solid transparent;
  border-radius: 9px;
  font-size: 11px;
  font-weight: 800;
  line-height: 1.2;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.04);
}

.status-ready {
  color: #8fd096;
  border-color: rgba(66, 148, 74, 0.38);
  background: rgba(66, 148, 74, 0.14);
}

.status-low {
  color: #ffd37d;
  border-color: rgba(255, 188, 70, 0.23);
  background: rgba(255, 174, 34, 0.09);
}

.status-empty {
  color: #ffaaa7;
  border-color: rgba(255, 112, 108, 0.22);
  background: rgba(255, 91, 87, 0.09);
}

.status-untracked {
  color: #c8cbc8;
  border-color: rgba(189, 203, 211, 0.17);
  background: rgba(189, 203, 211, 0.07);
}

.product-content {
  flex: 1;
  padding: 23px 2px 20px;
}

.product-content h4 {
  display: -webkit-box;
  margin: 0 0 10px;
  overflow: hidden;
  color: #f1f4f1;
  font-size: 17px;
  font-weight: 750;
  line-height: 1.35;
  letter-spacing: -0.015em;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.barcode {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  color: #939793;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", monospace;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
}

.barcode .v-icon {
  color: #7d817d !important;
}

.product-footer {
  padding-top: 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.09);
  color: #78bb7f;
  font-size: 13px;
  font-weight: 800;
}

.arrow-button {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  color: #82c088;
  background: rgba(66, 148, 74, 0.16);
  transition: transform 180ms ease, background-color 180ms ease;
}

.product-skeleton {
  min-height: 220px;
  overflow: hidden;
  border: 1px solid var(--stock-line);
  border-radius: 18px;
  background: var(--stock-panel) !important;
}

.product-skeleton >>> .v-skeleton-loader__image,
.product-skeleton >>> .v-skeleton-loader__heading,
.product-skeleton >>> .v-skeleton-loader__text {
  background: rgba(255, 255, 255, 0.08) !important;
}

.empty-state {
  min-height: 320px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 38px 22px;
  border: 1px dashed rgba(255, 255, 255, 0.18);
  border-radius: 20px;
  text-align: center;
  background: rgba(24, 25, 24, 0.5);
}

.empty-icon {
  width: 64px;
  height: 64px;
  margin-bottom: 18px;
  border: 1px solid var(--stock-line);
  border-radius: 20px;
  color: #969b96;
  background: rgba(255, 255, 255, 0.055);
}

.empty-state h2 {
  margin: 0;
  color: #e8ebe8;
  font-size: 21px;
}

.empty-state p {
  max-width: 420px;
  margin: 8px 0 11px;
  color: var(--stock-muted);
  font-size: 14px;
  line-height: 1.55;
}

.history-panel {
  min-height: 420px;
  padding: clamp(18px, 2.6vw, 34px);
  border: 1px solid var(--stock-line);
  border-radius: 22px;
  background: rgba(37, 38, 37, 0.94);
  box-shadow: 0 20px 52px rgba(0, 0, 0, 0.15);
}

.panel-heading {
  align-items: center;
  margin-bottom: 28px;
}

.panel-heading p {
  margin: 8px 0 0;
  color: var(--stock-muted);
  font-size: 13px;
}

.history-search {
  width: 330px;
  max-width: 100%;
  flex: 0 0 330px;
}

.history-loading {
  min-height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: var(--stock-muted);
  font-size: 13px;
}

.history-empty {
  min-height: 280px;
  border: 0;
  background: rgba(12, 13, 12, 0.24);
}

.history-cards {
  display: grid;
  gap: 12px;
}

.history-card {
  padding: 17px;
  border: 1px solid var(--stock-line);
  border-radius: 16px;
  background: rgba(43, 44, 43, 0.9);
}

.history-card-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.history-card-top > div {
  min-width: 0;
}

.history-date,
.table-date {
  color: #929692;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

.history-card h3 {
  margin: 5px 0 6px;
  color: #f0f2f0;
  font-size: 16px;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.history-meta {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(54px, auto);
  gap: 16px;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.09);
}

.history-meta div {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.history-meta span {
  color: #888c88;
  font-size: 11px;
  text-transform: uppercase;
}

.history-meta strong {
  color: #e1e4e1;
  font-size: 13px;
  overflow-wrap: anywhere;
}

.history-comment {
  margin: 14px 0 0;
  color: #b3b6b3;
  font-size: 13px;
  font-style: italic;
  line-height: 1.5;
}

.cancel-window {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 13px;
  color: #82bc87;
  font-size: 11px;
}

.cancel-window .v-icon {
  color: inherit !important;
}

.transparent-table {
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
  background: transparent !important;
}

.transparent-table >>> .v-data-table__wrapper {
  background: rgba(12, 13, 12, 0.2);
}

.transparent-table >>> table {
  border-collapse: separate;
  border-spacing: 0;
}

.transparent-table >>> th {
  height: 48px;
  color: #9fa29f !important;
  border-bottom: 1px solid var(--stock-line) !important;
  background: rgba(18, 34, 45, 0.94);
  font-size: 11px !important;
  font-weight: 800 !important;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.transparent-table >>> td {
  height: 68px;
  color: #d5d8d5;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07) !important;
  font-size: 13px;
}

.transparent-table >>> tbody tr:hover {
  background: rgba(66, 148, 74, 0.055) !important;
}

.transparent-table >>> .v-data-footer {
  color: #9fa29f;
  border-top: 1px solid var(--stock-line);
  background: rgba(29, 30, 29, 0.9);
}

.table-product {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.table-product strong {
  color: #f0f2f0;
}

.quantity-badge {
  min-width: 34px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  border-radius: 9px;
  color: #9bd3a1;
  background: rgba(66, 148, 74, 0.16);
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

.table-comment {
  color: #acafac;
}

.muted-dash,
.locked-state {
  color: #7f837f;
}

.icon-action {
  width: 44px !important;
  height: 44px !important;
  min-width: 44px !important;
  color: #b7bbb7 !important;
}

.danger-action {
  color: #ffaaa7 !important;
  background: rgba(255, 91, 87, 0.07);
}

.stock-sheet {
  --stock-line: rgba(255, 255, 255, 0.13);
  --stock-text: #f4f6f4;
  --stock-muted: #9fa29f;
  color: var(--stock-text);
  border: 1px solid rgba(255, 255, 255, 0.17) !important;
  border-radius: 24px !important;
  background: #242524 !important;
  box-shadow: 0 36px 90px rgba(0, 0, 0, 0.46) !important;
  overflow: hidden;
}

.scanner-sheet {
  max-height: calc(100vh - 24px);
  overflow-y: auto;
}

.sheet-header {
  min-height: 82px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 18px 22px 15px 26px;
  border-bottom: 1px solid var(--stock-line);
  background: rgba(43, 44, 43, 0.98);
}

.sheet-header h2 {
  margin: 4px 0 0;
  color: #f4f6f4;
  font-size: 21px;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.sheet-content {
  padding: 24px 26px !important;
}

.selected-product {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 14px;
  padding: 15px;
  border: 1px solid var(--stock-line);
  border-radius: 16px;
  background: rgba(12, 13, 12, 0.42);
}

.selected-product-icon {
  width: 46px;
  height: 46px;
  border-radius: 14px;
  color: #94b397;
  background: rgba(66, 148, 74, 0.14);
}

.selected-product-copy {
  min-width: 0;
}

.selected-product-copy > span:first-child {
  color: #76b77d;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.selected-product-copy h3 {
  margin: 3px 0 4px;
  color: #f0f3f0;
  font-size: 15px;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.booking-form {
  margin-top: 24px;
}

.form-field {
  margin-bottom: 5px !important;
}

.field-group {
  margin-bottom: 22px;
}

.field-group > label {
  display: block;
  margin-bottom: 8px;
  color: #bbc0bb;
  font-size: 12px;
  font-weight: 700;
}

.field-error {
  display: block;
  margin: 7px 4px 0;
  color: #ffaaa7;
  font-size: 11px;
  line-height: 1.35;
}

.quantity-control {
  height: 58px;
  display: grid;
  grid-template-columns: 52px minmax(80px, 1fr) 52px;
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 14px;
  background: rgba(15, 16, 15, 0.5);
  overflow: hidden;
}

.quantity-button {
  width: 52px !important;
  height: 56px !important;
  border-radius: 0 !important;
  color: #d8e8da !important;
}

.quantity-button:first-child {
  border-right: 1px solid var(--stock-line);
}

.quantity-button:last-child {
  border-left: 1px solid var(--stock-line);
}

.quantity-input >>> .v-input__slot {
  min-height: 56px !important;
  margin: 0 !important;
  color: #f5f7f5 !important;
  background: transparent !important;
  box-shadow: none !important;
}

.quantity-input >>> input {
  color: #f5f7f5 !important;
  font-size: 20px;
  font-weight: 800;
  text-align: center;
  font-variant-numeric: tabular-nums;
  -moz-appearance: textfield;
}

.quantity-input >>> input::-webkit-inner-spin-button,
.quantity-input >>> input::-webkit-outer-spin-button {
  margin: 0;
  -webkit-appearance: none;
}

.stock-projection {
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: -7px 0 22px;
  padding: 10px 13px;
  border: 1px solid rgba(66, 148, 74, 0.32);
  border-radius: 12px;
  color: #94d09b;
  background: rgba(66, 148, 74, 0.12);
}

.stock-projection span {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
}

.stock-projection .v-icon {
  color: inherit !important;
}

.stock-projection strong {
  font-size: 17px;
  font-variant-numeric: tabular-nums;
}

.stock-projection.warning {
  color: #ffb2ae;
  border-color: rgba(255, 91, 87, 0.22);
  background: rgba(255, 91, 87, 0.08);
}

.sheet-actions {
  min-height: 78px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 22px max(14px, env(safe-area-inset-bottom)) !important;
  border-top: 1px solid var(--stock-line);
  background: #292a29;
}

.cancel-action,
.submit-action {
  min-height: 48px;
  padding: 0 20px !important;
}

.cancel-action {
  color: #b3b6b3 !important;
}

.submit-action {
  min-width: 210px;
}

.scanner-body {
  padding: 22px;
}

.scanner-container {
  height: min(54vh, 410px);
  min-height: 280px;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.19);
  border-radius: 18px;
  background: #070807;
  overflow: hidden;
}

.live-qr-reader {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover !important;
}

.scanner-frame {
  width: 90%;
  height: 50%;
  position: absolute;
  top: 50%;
  left: 50%;
  border: 2px solid rgba(214, 241, 252, 0.78);
  border-radius: 17px;
  box-shadow: 0 0 0 999px rgba(7, 9, 7, 0.4), inset 0 0 24px rgba(66, 148, 74, 0.1);
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 3;
}

.scanner-laser {
  width: 90%;
  height: 2px;
  position: absolute;
  top: 27%;
  left: 5%;
  border-radius: 2px;
  background: #63ad6b;
  box-shadow: 0 0 12px rgba(66, 148, 74, 0.82);
  animation: scanning 2.4s ease-in-out infinite;
  pointer-events: none;
  z-index: 4;
}

.scanner-tip {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 13px;
  color: #adb1ad;
  background: rgba(255, 255, 255, 0.055);
}

.scanner-tip-icon {
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  border-radius: 11px;
  color: #83c08a;
  background: rgba(66, 148, 74, 0.14);
}

.scanner-tip p {
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
}

.scanner-tip strong {
  color: #e3e6e3;
}

.confirm-sheet {
  padding-top: 26px;
  text-align: center;
}

.confirm-icon {
  width: 54px;
  height: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 8px;
  border-radius: 17px;
  color: #ffd07a;
  background: rgba(255, 174, 34, 0.1);
}

.confirm-icon .v-icon {
  color: inherit !important;
}

.confirm-sheet >>> .v-card__title {
  justify-content: center;
  color: #f1f4f1;
  font-size: 21px;
  font-weight: 800;
}

.confirm-sheet >>> .v-card__text {
  padding: 2px 30px 26px;
  color: #aeb1ae;
  font-size: 14px;
  line-height: 1.6;
}

.confirm-sheet >>> .v-card__text strong {
  color: #e8ebe8;
}

.confirm-actions {
  justify-content: center;
}

.visually-hidden {
  width: 1px !important;
  height: 1px !important;
  position: absolute !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

@keyframes scanning {
  0%,
  100% {
    top: 27%;
    opacity: 0.65;
  }
  50% {
    top: 73%;
    opacity: 1;
  }
}

@media (hover: hover) {
  .primary-action:hover,
  .submit-action:hover {
    background: #4b9f53 !important;
  }

  .admin-nav-btn:hover,
  .secondary-action:hover {
    border-color: rgba(66, 148, 74, 0.72) !important;
    background: rgba(66, 148, 74, 0.1) !important;
  }

  .category-pill:hover {
    color: #e0e4e0;
    border-color: var(--stock-line-strong);
  }

  .product-card:hover {
    border-color: rgba(66, 148, 74, 0.58);
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);
    transform: translateY(-3px);
  }

  .product-card:hover .arrow-button {
    background: rgba(66, 148, 74, 0.22);
    transform: translateX(2px);
  }
}

.category-pill:focus,
.product-card:focus,
.marketing-stock-root .v-btn:focus,
.stock-sheet .v-btn:focus {
  border-color: rgba(66, 148, 74, 0.78);
  outline: 2px solid rgba(66, 148, 74, 0.68) !important;
  outline-offset: 2px;
}

.category-pill:focus-visible,
.product-card:focus-visible,
.marketing-stock-root .v-btn:focus-visible,
.stock-sheet .v-btn:focus-visible {
  outline: 3px solid rgba(66, 148, 74, 0.8) !important;
  outline-offset: 3px;
}

@media (min-width: 1440px) {
  .product-grid {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }
}

@media (max-width: 1199px) {
  .product-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 959px) {
  .stock-hero {
    grid-template-columns: 1fr;
    min-height: 0;
    gap: 30px;
  }

  .hero-side {
    align-items: stretch;
  }

  .admin-nav-btn {
    align-self: flex-start;
    order: 2;
  }

  .hero-metrics {
    max-width: 540px;
  }

  .command-panel {
    grid-template-columns: 1fr;
  }

  .product-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .panel-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .history-search {
    width: 100%;
    flex-basis: auto;
  }
}

@media (max-width: 599px) {
  .stock-page {
    padding: 10px;
    padding-bottom: max(24px, env(safe-area-inset-bottom));
  }

  .stock-hero {
    padding: 22px 18px 18px;
    border-radius: 21px;
  }

  .stock-hero::after {
    width: 240px;
    height: 240px;
    right: -130px;
    top: -110px;
  }

  .brand-line {
    margin-bottom: 20px;
  }

  .stock-hero h1 {
    font-size: clamp(34px, 11vw, 43px);
  }

  .hero-subtitle {
    margin-top: 16px;
    font-size: 14px;
  }

  .hero-side {
    gap: 16px;
  }

  .hero-metrics {
    order: 1;
  }

  .metric {
    padding: 14px 8px;
  }

  .metric-value {
    font-size: 21px;
  }

  .metric-label {
    font-size: 9px;
  }

  .admin-nav-btn {
    width: 100%;
    order: 2;
  }

  .tab-shell {
    margin: 12px 0 20px;
    padding: 4px;
    border-radius: 15px;
  }

  .tabs-nav >>> .v-tabs-bar {
    height: 48px;
  }

  .stock-tab {
    padding: 0 7px;
    font-size: 11px;
  }

  .stock-tab .v-icon {
    margin-right: 5px !important;
    font-size: 17px !important;
  }

  .tab-count {
    display: none;
  }

  .command-panel {
    gap: 11px;
    padding: 10px;
    border-radius: 17px;
  }

  .scan-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .scan-actions .v-btn {
    min-width: 0 !important;
    padding: 0 9px !important;
    font-size: 11px;
  }

  .scan-actions .v-icon {
    margin-right: 5px !important;
    font-size: 19px !important;
  }

  .category-rail {
    margin: 17px -10px 23px;
    padding: 1px 10px 7px;
  }

  .results-heading {
    align-items: flex-start;
    margin-bottom: 14px;
  }

  .result-note {
    display: none;
  }

  .results-heading h2 {
    font-size: 24px;
  }

  .category-section + .category-section {
    margin-top: 30px;
  }

  .product-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .product-card,
  .product-skeleton {
    min-height: 184px;
  }

  .product-card {
    padding: 15px;
    border-radius: 16px;
  }

  .product-content {
    padding: 17px 2px 15px;
  }

  .product-content h4 {
    font-size: 16px;
  }

  .empty-state {
    min-height: 290px;
  }

  .history-panel {
    padding: 17px 14px;
    border-radius: 18px;
  }

  .panel-heading {
    gap: 18px;
    margin-bottom: 20px;
  }

  .panel-heading h2 {
    font-size: 24px;
  }

  .history-card {
    padding: 15px;
  }

  .stock-sheet {
    border: 0 !important;
    border-radius: 0 !important;
  }

  .scanner-sheet {
    min-height: 100%;
    max-height: none;
  }

  .sheet-header {
    min-height: 76px;
    padding: max(15px, env(safe-area-inset-top)) 12px 13px 18px;
  }

  .sheet-content {
    padding: 20px 16px !important;
  }

  .selected-product {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .selected-product > .stock-status {
    grid-column: 1 / -1;
    justify-self: start;
  }

  .sheet-actions {
    display: grid;
    grid-template-columns: minmax(0, 0.72fr) minmax(0, 1.4fr);
    padding: 12px 12px max(12px, env(safe-area-inset-bottom)) !important;
  }

  .sheet-actions .v-btn {
    min-width: 0 !important;
    padding: 0 10px !important;
    font-size: 12px;
  }

  .scanner-body {
    padding: 14px;
  }

  .scanner-container {
    height: min(61vh, 500px);
    min-height: 260px;
    border-radius: 16px;
  }

  .confirm-sheet {
    margin: 10px;
    border: 1px solid rgba(255, 255, 255, 0.17) !important;
    border-radius: 22px !important;
  }

  .confirm-actions {
    grid-template-columns: 1fr 1.2fr;
  }
}

@media (max-height: 620px) and (orientation: landscape) {
  .scanner-sheet {
    height: 100vh;
    max-height: 100vh;
    overflow-y: auto;
  }

  .scanner-sheet .sheet-header {
    min-height: 64px;
    padding-top: 10px;
    padding-bottom: 10px;
  }

  .scanner-body {
    padding: 10px 14px 14px;
  }

  .scanner-container {
    height: 48vh;
    min-height: 150px;
  }

  .scanner-tip {
    margin-top: 9px;
    padding: 9px 12px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .marketing-stock-root *,
  .marketing-stock-root *::before,
  .marketing-stock-root *::after,
  .stock-sheet,
  .stock-sheet *,
  .stock-sheet *::before,
  .stock-sheet *::after {
    scroll-behavior: auto !important;
    transition-duration: 0.01ms !important;
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
  }
}
</style>

<style>
.stock-products-menu {
  overflow: hidden;
  border: 1px solid #3b3d3b;
  border-radius: 13px;
  background: #2a2b2a !important;
  box-shadow: 0 20px 54px rgba(0, 0, 0, 0.48) !important;
}

.stock-products-menu .v-list {
  padding: 6px;
  background: #2a2b2a !important;
}

.stock-products-menu .v-list-item {
  min-height: 44px;
  border-radius: 9px;
  color: #e4e7e4 !important;
}

.stock-products-menu .v-list-item--active,
.stock-products-menu .v-list-item:hover {
  color: #edf5ee !important;
  background: rgba(66, 148, 74, 0.16) !important;
}

@media (prefers-reduced-motion: reduce) {
  .dialog-bottom-transition-enter-active,
  .dialog-bottom-transition-leave-active,
  .dialog-transition-enter-active,
  .dialog-transition-leave-active {
    transition-duration: 0.01ms !important;
    animation: none !important;
  }
}
</style>
